/** Parses user input or stored value to a non-negative amount (max 2 decimals). */
export function parseOfferVolumeAmount(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw >= 0 ? raw : null;
  }
  let s = String(raw).trim();
  if (!s) {
    return null;
  }
  s = s.replace(/[^\d,.]/g, '');
  if (!s) {
    return null;
  }
  if (s.includes(',')) {
    const commaIdx = s.lastIndexOf(',');
    const intPart = s.slice(0, commaIdx).replace(/\./g, '').replace(/,/g, '');
    const decPart = s.slice(commaIdx + 1).replace(/[^\d]/g, '').slice(0, 2);
    s = decPart.length ? `${intPart || '0'}.${decPart}` : intPart || '0';
  } else if (s.includes('.')) {
    const dotIdx = s.lastIndexOf('.');
    const after = s.slice(dotIdx + 1);
    if (/^\d{1,2}$/.test(after) && s.indexOf('.') === dotIdx) {
      const intPart = s.slice(0, dotIdx).replace(/\./g, '');
      s = `${intPart || '0'}.${after}`;
    } else {
      s = s.replace(/\./g, '');
    }
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.round(n * 100) / 100;
}

/** Canonical stored amount (EUR, max 2 decimals). */
export function offerVolumeToStorage(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function offerVolumeStorageEquals(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean {
  const na = parseOfferVolumeAmount(a);
  const nb = parseOfferVolumeAmount(b);
  if (na === null && nb === null) {
    return true;
  }
  if (na === null || nb === null) {
    return false;
  }
  return na === nb;
}

/** German display formatting while typing or from stored value. */
export function formatOfferVolumeDisplay(raw: string | number | null | undefined): string {
  const amount = parseOfferVolumeAmount(raw);
  if (amount === null) {
    return '';
  }
  return amount.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

/** Restricts input to digits only; applies de-DE grouping on the fly. */
export function sanitizeOfferVolumeDraftInput(raw: string): string {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return Number(digits).toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

const OFFER_VOLUME_INPUT_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End'
]);

/** Blocks non-digit keys in price fields (allow Ctrl/Cmd shortcuts). */
export function isOfferVolumeInputKeyAllowed(event: KeyboardEvent): boolean {
  if (OFFER_VOLUME_INPUT_KEYS.has(event.key)) {
    return true;
  }
  if (event.ctrlKey || event.metaKey) {
    return true;
  }
  return /^\d$/.test(event.key);
}
